import { useEffect, useState } from 'react';

import type { Maybe } from 'graphql/jsutils/Maybe';

import { fg } from '@atlaskit/platform-feature-flags';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { useLazyQueryLight, type UseQueryLightError } from '@atlaskit/teams-client';
import { AnalyticsEventActions, AnalyticsEventSource, useAnalytics } from '@atlassian/ptc-common';

import ReportingLinesQueryWithAccountStatus from './ReportingLinesQueryWithAccountStatus.graphql';
import { type ReportingLines, type ReportingLinesUser } from './types';

export interface ReportingLinesResponse {
	isLoading: boolean;
	reportingLines?: ReportingLines;
	error?: UseQueryLightError;
}

export const useReportingLines = (selectedUserId: string): ReportingLinesResponse => {
	const analytics = useAnalytics();
	const { fireEvent } = useAnalyticsEvents();

	const [getReportingLines, { data, loading, error }] = useLazyQueryLight<
		{ aaid: string },
		{
			reportingLines: ReportingLines;
		}
	>(ReportingLinesQueryWithAccountStatus, {
		variables: {
			aaid: selectedUserId ?? '',
		},
		operationName: 'ReportingLines',
		serviceUrl: '/gateway/api/watermelon/graphql',
	});
	const [fallbackData, setFallback] = useState<ReportingLinesResponse | undefined>({
		isLoading: false,
		reportingLines: {},
		error: undefined,
	});

	useEffect(() => {
		// Only fetch reporting lines if site (or the org containing this site) has a team central workspace
		getReportingLines();
		setFallback(undefined);
	}, [getReportingLines, selectedUserId]);

	if (fallbackData) {
		return fallbackData;
	}

	const filterNonactiveUsers = (
		users?: Maybe<ReportingLinesUser[]>,
	): Maybe<ReportingLinesUser[]> => {
		return users?.filter((user) => {
			const isActiveAccountStatus = user.pii?.accountStatus === 'active';

			if (!isActiveAccountStatus) {
				if (fg('ptc-enable-ptc-common-analytics-refactor')) {
					// @ts-ignore - hyphenated action subjects are not supported in @atlaskit/teams-app-internal-analytics
					fireEvent('operational.reporting-lines-user.failed', {
						aaid: user.accountIdentifier || 'undefined',
					});
				} else {
					analytics.pushOperationalEvent({
						source: AnalyticsEventSource.CLIENT_METRICS,
						actionSubject: 'reporting-lines-user',
						action: AnalyticsEventActions.FAILED,
						attributes: {
							aaid: user.accountIdentifier || 'undefined',
						},
					});
				}
			}

			return isActiveAccountStatus;
		});
	};

	const filteredReportingLines = data?.reportingLines
		? {
				managers: filterNonactiveUsers(data.reportingLines.managers),
				peers: filterNonactiveUsers(data.reportingLines.peers),
				reports: filterNonactiveUsers(data.reportingLines.reports),
				user: data.reportingLines.user,
				lastModifiedDate: data.reportingLines.lastModifiedDate,
			}
		: data?.reportingLines;

	return {
		isLoading: loading,
		reportingLines: filteredReportingLines,
		error: error as unknown as UseQueryLightError,
	};
};
